import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { googleWorkspace } from '@/lib/googleWorkspace';
import { GoogleWorkspaceError } from '@/lib/googleWorkspaceLogic';
import { useLocalization } from '@/hooks/useLocalization';

// Registered as the Google OAuth redirect URI (single fixed URL; Google does not
// support per-tenant-slug paths), same pattern as MicrosoftConsentCallback. The
// Supabase session is still in localStorage here, so the call is authenticated.
export default function GoogleWorkspaceCallback() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'working' | 'success' | 'error'>('working');
  const [message, setMessage] = useState(t('Finishing Google connection…'));
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const captured = useRef<{ code: string | null; state: string | null; error: string | null } | null>(null);
  const ran = useRef(false);

  // On mount: keep code/state in memory only, strip them from the URL/history
  // immediately, and stop the browser sending this URL as a Referer.
  useEffect(() => {
    if (!captured.current) {
      const params = new URLSearchParams(window.location.search);
      captured.current = { code: params.get('code'), state: params.get('state'), error: params.get('error') };
      window.history.replaceState(null, '', window.location.pathname);
    }
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    if (authLoading || ran.current || !captured.current) return;
    ran.current = true;
    const params = captured.current;
    captured.current = { code: null, state: null, error: null };

    (async () => {
      // Slug lookup is best-effort: a failure here must not block the callback
      // (the single-use code) or leave the page stuck in "working".
      try {
        const { data: tenantId } = await supabase.rpc('auth_tenant_id');
        if (tenantId) {
          const { data: tenant } = await supabase.from('tenants').select('slug').eq('id', tenantId).maybeSingle();
          setTenantSlug(tenant?.slug ?? null);
        }
      } catch {
        setTenantSlug(null);
      }
      try {
        await googleWorkspace.callback(params);
        setStatus('success');
        setMessage(t('Google connected successfully.'));
      } catch (e) {
        setStatus('error');
        // Server messages are fixed, non-sensitive strings; anything else is generic.
        setMessage(e instanceof GoogleWorkspaceError ? e.message : t('Could not complete the Google connection.'));
      }
    })();
  }, [authLoading, t]);

  useEffect(() => {
    if (status === 'success' && tenantSlug) {
      const timer = setTimeout(() => navigate(`/${tenantSlug}/settings?tab=integrations`, { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
  }, [status, tenantSlug, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-sm text-center space-y-4" role="status" aria-live="polite">
        {status === 'working' && <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" aria-hidden="true" />}
        {status === 'success' && <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" aria-hidden="true" />}
        {status === 'error' && <XCircle className="h-10 w-10 mx-auto text-destructive" aria-hidden="true" />}
        <p className="text-sm text-muted-foreground">{message}</p>
        {status === 'error' && (
          <button
            className="text-sm underline text-primary"
            onClick={() => navigate(tenantSlug ? `/${tenantSlug}/settings?tab=integrations` : "/auth", { replace: true })}
          >
            {t('Back to Settings')}
          </button>
        )}
      </div>
    </div>
  );
}
